/**
 * CaptureGuidanceCard — the one thing an athlete reads before they film
 * (viewpoint observability Phase 1, design §7A item 3).
 *
 * What changed and why:
 *   The card used to translate the coach's `camera_angle_setup` into a
 *   sentence ("Film from the side (athlete facing left or right)") through a
 *   CAMERA_VIEW_LABELS map. That map was both incomplete (it had no entry for
 *   the `other` / unset views, so most templates got no direction at all) and
 *   the wrong instruction: what actually matters is matching the coach's demo,
 *   including which side of the body faces the camera. A still frame from the
 *   demo says that in one glance, and no words can say it as well.
 *
 *   So the card now shows a frame of the coach's own reference video and the
 *   owner-approved copy from docs/COACH_FACING_COPY_STANDARD.md §3 — what to
 *   do, why it matters, and how to set the phone up. `cameraAngleSetup` and
 *   the label map are RETIRED.
 *
 * Visibility: the card itself renders UNCONDITIONALLY — every athlete about to
 * film needs the framing rules and the phone setup, whatever the template
 * says. Only the ANGLE guidance (the "same side toward the camera" sentence,
 * the still, and the back-camera note, all of which refer to the picture) is
 * gated on the template declaring a facing direction, because without one
 * there is no side to match. A frontal or diagonal template with no declared
 * facing still gets guidance.
 *
 * Dismissal is PER TEMPLATE (`capture-guidance-dismissed:{templateId}`):
 * dismissing the guidance for one movement must not hide it for a different
 * movement filmed from a different angle. The old single global key is gone.
 */
import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { useMediaUrl } from '@/hooks/useMediaUrl';
import { captureGuidanceDismissKey } from './captureGuidanceKey';

export interface CaptureGuidanceCardProps {
  /**
   * The template's `facing_direction` — the way the performer's front points
   * in the coach's demo, as captured. When absent, the card still renders;
   * only the angle guidance (which needs a side to match) is left out.
   */
  facingDirection?: 'left' | 'right' | null;
  /** Template id — scopes the "don't show again" flag to this movement. */
  templateId?: string | null;
  /** The coach's reference video id — the still is pulled from this video. */
  referenceVideoId?: string | null;
  /**
   * Frame to show from the reference video. The caller passes the middle of
   * the template's trimmed window, where the movement is most recognisable.
   * Omitted → the video's first frame.
   */
  midFrame?: number;
  /** Force the card open even when the user previously dismissed it.
   *  Used by the quality-feedback "Try again" flow so guidance re-appears
   *  after a gate failure. */
  forceOpen?: boolean;
  /** Called when the user clicks Dismiss or "Don't show again". */
  onDismiss?: () => void;
  className?: string;
}

/**
 * The owner-approved capture sentence (docs/COACH_FACING_COPY_STANDARD.md §3,
 * quoted by SYNTHESIS_V29 §7A item 3 and the Phase 1 contract). Split into
 * what / why / how so each renders as its own line — a wall of text is the
 * one thing an athlete standing in a studio will not read.
 *
 * The split is also the gate: `what`, `why` and `flipped` all refer to the
 * picture of the coach's demo, so they only make sense once the template
 * declares a facing direction. `setup` is true of every recording and is
 * always shown. The §3 sentence is unchanged — only where its clauses render.
 */
const CAPTURE_COPY = {
  /** ANGLE-GATED — names the side of the body to turn toward the camera. */
  what:
    "Film from the same angle as your coach's demo, with the same side of " +
    'your body toward the camera as in the picture.',
  /** ANGLE-GATED — why that angle and not another. */
  why: "That's the angle the checkpoints were set up for.",
  /** ALWAYS — true of any recording, with or without a declared facing. */
  setup:
    'Put the phone on something steady at about hip height, a few metres ' +
    'back, and zoom rather than step closer.',
  /** ANGLE-GATED — "the picture" is the still, which is itself gated. */
  flipped:
    'If the picture looks flipped compared with your video, re-film with ' +
    'the back camera.',
} as const;

/**
 * Framing rules that are independent of the camera angle. Kept from the
 * card's previous tip list: a limb that leaves the frame cannot be measured,
 * and a second person in frame can be tracked instead of the athlete.
 */
const FRAMING_RULES = [
  'Keep your whole body in frame for the whole movement — if a hand or foot ' +
    'leaves the picture, we cannot measure it.',
  'Film one person only. Keep studio mirrors, other dancers and bystanders ' +
    'out of frame, or we may follow the wrong person.',
] as const;

function CaptureGuidanceCard({
  facingDirection,
  templateId,
  referenceVideoId,
  midFrame,
  forceOpen = false,
  onDismiss,
  className,
}: CaptureGuidanceCardProps) {
  const [visible, setVisible] = useState(false);
  const storageKey = captureGuidanceDismissKey(templateId);

  // Signed thumbnail URL for the coach's reference video. Null while the
  // media token loads or when no reference video id is known — the card then
  // renders its copy without a picture rather than a broken image.
  const { thumbnailUrl } = useMediaUrl(referenceVideoId || undefined);
  const stillUrl = thumbnailUrl(midFrame, 480);

  // On mount (and whenever the template changes): show unless this template's
  // guidance was already dismissed, and not force-overridden.
  useEffect(() => {
    if (forceOpen) {
      setVisible(true);
      return;
    }
    try {
      const dismissed = localStorage.getItem(storageKey);
      setVisible(dismissed !== 'true');
    } catch {
      // localStorage unavailable — show by default
      setVisible(true);
    }
  }, [forceOpen, storageKey]);

  function handleDismiss(persist: boolean) {
    setVisible(false);
    if (persist) {
      try {
        localStorage.setItem(storageKey, 'true');
      } catch {
        // ignore
      }
    }
    onDismiss?.();
  }

  if (!visible) return null;

  // With no declared facing direction there is no side to match, so the angle
  // guidance and its picture are left out — but the card still renders.
  const hasAngleGuidance = !!facingDirection;

  return (
    <div
      role="note"
      aria-label="Capture guidance"
      data-testid="capture-guidance-card"
      className={cn(
        'rounded-lg border border-info/30 bg-info/10 p-4',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* Camera icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 flex-shrink-0 text-info"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
            />
          </svg>
          <h4 className="text-sm font-semibold text-text-primary">Before you film</h4>
        </div>

        <button
          type="button"
          onClick={() => handleDismiss(false)}
          className="flex-shrink-0 rounded p-0.5 text-text-muted hover:text-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 transition-colors"
          aria-label="Dismiss guidance"
          data-testid="capture-guidance-dismiss"
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Picture + copy. Stacks on phones (16px page gutter already applied by
          the page shell), side by side from sm up. */}
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start">
        {hasAngleGuidance && stillUrl && (
          <img
            src={stillUrl}
            data-testid="capture-guidance-still"
            alt={
              "A frame from your coach's demo. Match this angle, with the " +
              'same side of your body toward the camera.'
            }
            className="w-full flex-shrink-0 rounded-md border border-border-subtle object-cover sm:w-40"
          />
        )}

        <div className="space-y-1.5 text-sm leading-relaxed">
          {hasAngleGuidance && (
            <>
              <p
                data-testid="capture-guidance-angle"
                className="font-medium text-text-primary"
              >
                {CAPTURE_COPY.what}
              </p>
              <p className="text-text-secondary">{CAPTURE_COPY.why}</p>
            </>
          )}
          {/* The lead line when there is no angle guidance to lead with. */}
          <p
            data-testid="capture-guidance-setup"
            className={
              hasAngleGuidance
                ? 'text-text-secondary'
                : 'font-medium text-text-primary'
            }
          >
            {CAPTURE_COPY.setup}
          </p>
          {hasAngleGuidance && (
            <p className="text-text-secondary">{CAPTURE_COPY.flipped}</p>
          )}
        </div>
      </div>

      {/* The two framing rules that are not about the angle. Carried over from
          the card's previous tip list — each prevents a known failure mode
          (a cropped limb cannot be measured; a second person in frame can be
          tracked instead of the athlete), so they stay. */}
      <ul className="mt-3 space-y-1.5">
        {FRAMING_RULES.map((rule) => (
          <li key={rule} className="flex items-start gap-2 text-sm text-text-secondary">
            <span className="mt-0.5 flex-shrink-0 text-info" aria-hidden="true">
              &#x25CF;
            </span>
            <span>{rule}</span>
          </li>
        ))}
      </ul>

      {/* Don't show again */}
      <button
        type="button"
        onClick={() => handleDismiss(true)}
        className="mt-3 text-xs text-text-muted underline underline-offset-2 hover:text-text-secondary transition-colors"
        data-testid="capture-guidance-dont-show-again"
      >
        Don't show again
      </button>
    </div>
  );
}

export default CaptureGuidanceCard;
